import { Children, isValidElement } from 'react';

/** @typedef {'note' | 'warning' | 'important'} CalloutType */

export const CODE_COLLAPSE_LINE_THRESHOLD = 200;

const CALLOUT_PATTERN = /^(note|warning|important)\s*:?\s*(.*)$/is;

export function normalizeCitationRef(value) {
    const raw = String(value || '').trim();
    const bracketed = raw.match(/^\[(\d+)\]$/);
    if (bracketed) return bracketed[1];
    const numeric = raw.match(/^(\d+)$/);
    return numeric ? numeric[1] : null;
}

export function findCitationByRef(citations, ref) {
    const normalized = normalizeCitationRef(ref);
    if (!normalized) return null;
    return (Array.isArray(citations) ? citations : []).find(c => normalizeCitationRef(c?.ref ?? c) === normalized) || null;
}

export function linkifyCitationRefs(text, citations, citationScope) {
    if (!citationScope || !Array.isArray(citations) || citations.length === 0) return text;
    const availableRefs = new Set(citations.map(c => normalizeCitationRef(c?.ref ?? c)).filter(Boolean));
    if (availableRefs.size === 0) return text;

    const normalizedText = text.replace(/\[(\d+)\]\s*-\s*\[(\d+)\]/g, (match, startRef, endRef) => {
        if (!availableRefs.has(startRef) || !availableRefs.has(endRef)) return match;
        return `[${startRef}], [${endRef}]`;
    });

    return normalizedText.replace(/\[(\d+)\]/g, (match, ref) => {
        if (!availableRefs.has(ref)) return match;
        return `[${match}](#ai-warden-${citationScope}-citation-${ref})`;
    });
}

export function isSafeUrl(href) {
    if (!href || typeof href !== 'string') return false;
    const trimmed = href.trim();
    if (!trimmed || trimmed.startsWith('javascript:') || trimmed.startsWith('data:text/html')) return false;
    if (trimmed.startsWith('#') || trimmed.startsWith('/')) return true;
    try {
        const url = new URL(trimmed, window.location.origin);
        return ['http:', 'https:', 'mailto:'].includes(url.protocol);
    } catch {
        return false;
    }
}

export function isExternalHref(href) {
    if (!href || typeof href !== 'string') return false;
    if (href.startsWith('#') || href.startsWith('/')) return false;
    try {
        const url = new URL(href, window.location.origin);
        return url.origin !== window.location.origin;
    } catch {
        return false;
    }
}

export function isClosedFenceBlock(content, language) {
    if (!content || !language) return false;
    const fence = String(language).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const pattern = new RegExp('```' + fence + '\\s*[\\s\\S]*?```', 'm');
    return pattern.test(content);
}

export function normalizeCodeLanguage(language) {
    const raw = String(language || '').trim().toLowerCase();
    const aliases = {
        js: 'javascript',
        ts: 'typescript',
        py: 'python',
        sh: 'bash',
        shell: 'bash',
        yml: 'yaml',
        md: 'markdown',
    };
    return aliases[raw] || raw || 'text';
}

export function flattenMarkdownChildren(children) {
    return Children.toArray(children)
        .map(child => {
            if (typeof child === 'string' || typeof child === 'number') return String(child);
            if (isValidElement(child)) return flattenMarkdownChildren(child.props.children);
            return '';
        })
        .join('');
}

/** @returns {{ type: CalloutType, body: string } | null} */
export function parseCalloutFromBlockquote(children) {
    const text = flattenMarkdownChildren(children).trim();
    const match = text.match(CALLOUT_PATTERN);
    if (!match) return null;
    const type = match[1].toLowerCase();
    if (type !== 'note' && type !== 'warning' && type !== 'important') return null;
    return { type, body: match[2].trim() };
}

export function buildMarkdownRenderCacheKey(content, citations, citationScope, streaming = false) {
    const citationKey = (Array.isArray(citations) ? citations : [])
        .map(c => normalizeCitationRef(c?.ref ?? c))
        .filter(Boolean)
        .join(',');
    return `${streaming ? 'stream|' : ''}${citationScope || ''}|${citationKey}|${typeof content === 'string' ? content : ''}`;
}

export async function copyTextToClipboard(text) {
    const value = String(text || '');
    if (!value) return false;
    if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
        return true;
    }
    const textarea = document.createElement('textarea');
    textarea.value = value;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'absolute';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(textarea);
    return ok;
}
